import { IntakeData } from '../context/IntakeContext'

export function generateIntakeHTML(data: IntakeData): string {
  const styles = `
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
    h1 { color: #007AFF; border-bottom: 2px solid #007AFF; padding-bottom: 10px; }
    h2 { color: #1a1a1a; margin-top: 30px; background-color: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 4px solid #007AFF; }
    h3 { color: #444; margin-top: 20px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th, td { padding: 10px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; }
    th { width: 35%; color: #666; font-weight: normal; }
    td { font-weight: bold; }
    .item-box { border: 1px solid #eee; padding: 15px; margin-bottom: 15px; border-radius: 5px; background: #fafafa; }
    .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999; text-align: center; }
  `

  const renderRow = (label: string, value: any) => {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return ''
    }

    let displayValue = value
    if (typeof value === 'boolean') displayValue = value ? 'Ja' : 'Nee'
    else if (value === 'yes') displayValue = 'Ja'
    else if (value === 'no') displayValue = 'Nee'
    else if (Array.isArray(value)) displayValue = value.join(', ')

    return `
      <tr>
        <th>${label}</th>
        <td>${displayValue}</td>
      </tr>
    `
  }

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Noord Digital Intake - ${data.companyName || data.contactName}</title>
      <style>${styles}</style>
    </head>
    <body>
      <h1>Nieuwe Website Intake</h1>
      <p>Datum: ${new Date().toLocaleDateString('nl-NL')}</p>

      <h2>1. Contactgegevens</h2>
      <table>
        ${renderRow('Naam', data.contactName)}
        ${renderRow('Functie / Rol', data.contactRole)}
        ${renderRow('E-mailadres', data.contactEmail)}
        ${renderRow('Telefoonnummer', data.contactPhone)}
        ${renderRow('Eigenaar', data.isOwner)}
        ${renderRow('Uiteindelijke beslisser', data.isDecisionMaker)}
      </table>
  `

  if (data.isDecisionMaker === 'no' && data.owners.length > 0) {
    html += `<h3>Overige Beslissers / Contactpersonen</h3>`
    data.owners.forEach((owner, i) => {
      html += `
        <div class="item-box">
          <strong>Contactpersoon ${i + 1}</strong><br>
          Naam: ${owner.name}<br>
          Rol: ${owner.role}<br>
          E-mail: ${owner.email}<br>
          Telefoon: ${owner.phone}
        </div>
      `
    })
  }

  html += `
      <h2>2. Team</h2>
      <table>
        ${renderRow('Team tonen op website', data.showTeamOnWebsite)}
        ${renderRow("Foto's beschikbaar", data.teamHasPhotos)}
      </table>
  `

  if (data.teamMembers.length > 0) {
    html += `<h3>Teamleden</h3>`
    data.teamMembers.forEach((member) => {
      html += `
        <div class="item-box">
          <strong>${member.name}</strong> (${member.role})<br>
          Afdeling: ${member.department || '-'}<br>
          E-mail: ${member.email || '-'}<br>
          Telefoon: ${member.phone || '-'}<br>
          Bio: ${member.bio || '-'}
        </div>
      `
    })
  }

  html += `
      <h2>3. Bedrijfsgegevens</h2>
      <table>
        ${renderRow('Bedrijfsnaam', data.companyName)}
        ${renderRow('Handelsnaam', data.tradeName)}
        ${renderRow('KVK Nummer', data.cocNumber)}
        ${renderRow('BTW Nummer', data.vatNumber)}
        ${renderRow('Hoofdvestiging Straat', data.mainStreet)}
        ${renderRow('Hoofdvestiging Postcode', data.mainPostcode)}
        ${renderRow('Hoofdvestiging Stad', data.mainCity)}
        ${renderRow('Hoofdvestiging Provincie', data.mainProvince)}
        ${renderRow('Hoofdvestiging Land', data.mainCountry)}
        ${renderRow('Algemeen Telefoonnummer', data.companyPhone)}
        ${renderRow('Algemeen E-mailadres', data.companyEmail)}
        ${renderRow('Huidige Website', data.existingWebsiteUrl)}
        ${renderRow('Meerdere locaties', data.multipleLocations)}
        ${renderRow('Locatie strategie', data.locationStrategy)}
      </table>
  `

  if (data.multipleLocations === 'yes' && data.locations.length > 0) {
    html += `<h3>Locaties</h3>`
    data.locations.forEach((loc, i) => {
      html += `
        <div class="item-box">
          <strong>${loc.name || `Locatie ${i + 1}`}</strong><br>
          Adres: ${loc.street}, ${loc.postcode} ${loc.city}, ${loc.country}<br>
          Tonen op website: ${loc.showOnWebsite === 'yes' ? 'Ja' : 'Nee'}
        </div>
      `
    })
  }

  html += `
      <h2>4. Verhaal & Positionering</h2>
      <table>
        ${renderRow("Wat maakt u anders (USP's)", data.difference)}
        ${renderRow('Kernwaarden', data.coreValues)}
        ${renderRow('Bedrijfsgevoel', data.companyFeel)}
        ${renderRow('Bedrijfsgevoel (Anders)', data.companyFeelOther)}
        ${renderRow('Absoluut vermijden', data.mustAvoid)}
      </table>

      <h2>5. Juridisch</h2>
      <table>
        ${renderRow('Heeft juridische documenten', data.hasLegalDocs)}
        ${renderRow('Aanpak documenten', data.legalDocsApproach)}
        ${renderRow('Beschikbare documenten', data.legalDocsList)}
        ${renderRow('Benodigde documenten', data.legalDocsNeeded)}
      </table>
  `

  const uploadedDocs = Object.entries(data.legalDocs).filter(
    ([_, doc]) => doc.file,
  )
  if (uploadedDocs.length > 0) {
    html += `<h3>Geüploade Juridische Documenten</h3><ul>`
    uploadedDocs.forEach(([key, doc]) => {
      html += `<li><a href="${doc.file}" target="_blank">${key} downloaden</a></li>`
    })
    html += `</ul>`
  }

  html += `
      <h2>6. Branding & Huisstijl</h2>
      <table>
        ${renderRow('Heeft visuele identiteit', data.hasIdentity)}
        ${renderRow('Typografie wens', data.typographyChoice)}
        ${renderRow('Logo ideeën', data.logoIdeas)}
      </table>
  `

  if (data.preferredColors.length > 0) {
    html += `<h3>Merkkleuren</h3><ul>`
    data.preferredColors.forEach((color) => {
      html += `<li>${color.name}: ${color.hex} <span style="display:inline-block;width:15px;height:15px;background-color:${color.hex};border:1px solid #ccc;border-radius:3px;vertical-align:middle;margin-left:5px;"></span></li>`
    })
    html += `</ul>`
  }

  const uploadedBranding = Object.entries(data.brandingAssets).filter(
    ([_, asset]) => asset.file,
  )
  if (uploadedBranding.length > 0 || data.logoSketchUpload) {
    html += `<h3>Geüploade Branding Bestanden</h3><ul>`
    uploadedBranding.forEach(([key, asset]) => {
      html += `<li><a href="${asset.file}" target="_blank">${key} downloaden</a></li>`
    })
    if (data.logoSketchUpload) {
      html += `<li><a href="${data.logoSketchUpload}" target="_blank">Logo schets/inspiratie downloaden</a></li>`
    }
    html += `</ul>`
  }

  html += `
      <h2>7. Doelen & Type Website</h2>
      <table>
        ${renderRow('Primair doel', data.mainGoal)}
        ${renderRow('Doel (Anders)', data.mainGoalOther)}
        ${renderRow('Type website', data.websiteType)}
      </table>

      <h2>8. Pagina's & Content</h2>
      <table>
        ${renderRow("Gewenste pagina's", data.neededPages)}
        ${renderRow("Extra pagina's (Anders)", data.customPageOther)}
        ${renderRow('Content aanlevering', data.existingContent)}
        ${renderRow('Fotografie', data.createContent)}
      </table>

      <h2>9. Techniek & Hosting</h2>
      <table>
        ${renderRow('Heeft domeinnaam', data.domainOwned)}
        ${renderRow('Huidige domeinnaam', data.domainName)}
        ${renderRow('Domein host', data.domainHost)}
        ${renderRow('Domein beheer', data.domainManagement)}
        ${renderRow('Derde partij beheerder', data.domainThirdParty)}
        ${renderRow('Gewenste domeinnamen', data.desiredDomains)}
        ${renderRow('Hosting voorkeur', data.hostingActive)}
      </table>

      <h2>10. SEO & Marketing</h2>
      <table>
        ${renderRow('Belang van SEO', data.seoServices)}
        ${renderRow('Zoektermen', data.seoSearchTerms)}
        ${renderRow('Zoekwoordenonderzoek', data.seoKeywordResearch)}
        ${renderRow('Blogposts schrijven', data.seoBlogposts)}
        ${renderRow('Blog frequentie', data.seoBlogFrequency)}
        ${renderRow('SEO Silo', data.seoSilo)}
        ${renderRow('Actieve updates', data.seoActiveUpdates)}
        ${renderRow('Heatmaps', data.seoHeatmapsUrgency)}
        ${renderRow('A/B Testing', data.seoABTestingUrgency)}
        ${renderRow('Training gewenst', data.seoTraining)}
        ${renderRow('Google Bedrijfsprofiel hulp', data.seoGoogleBusiness)}
        ${renderRow('Google taken', data.seoGoogleBusinessTasks)}
        ${renderRow('Apple Bedrijfsprofiel hulp', data.seoAppleBusiness)}
        ${renderRow('Apple taken', data.seoAppleBusinessTasks)}
        ${renderRow('Google Ads', data.seoGoogleAds)}
        ${renderRow('Social Ads', data.seoSocialAds)}
      </table>

      <h2>11. Planning</h2>
      <table>
        ${renderRow('Gewenste live datum', data.launchDate)}
        ${renderRow('Opmerkingen', data.remarks)}
      </table>

      <div class="footer">
        Dit document is automatisch gegenereerd via de Noord Digital Intake.
      </div>
    </body>
    </html>
  `

  return html
}
